import { FieldArray, useFormikContext } from "formik";
import { useState } from "react";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import Callout from "../UI/Callout";
import IconButton from "../UI/IconButton";
import Button from "../UI/Button";
import Accordion, { AccordionItem } from "../UI/Accordion";
import Text from "../UI/Text";
// @ts-expect-error pure js
import TextInput from "../UI/TextInput";
import {
  BANK_AFFECTATION_TYPE_FILTERS,
  BalanceDomain,
} from "../../utils/constants/balances";
// @ts-expect-error pure js
import { currencyFormatter } from "../../api/Functions";
// @ts-expect-error pure js
import usePropertyFolders from "../../hooks/use-property-folders";
import BalanceSplitFields from "../Balance/BalanceSplitFields";
import BalanceTargetFields from "../Balance/BalanceTargetFields";
// @ts-expect-error pure js
import { useBalanceTypes } from "../../hooks/use-balances";
// @ts-expect-error pure js
import { setBalanceAmountSignFromType } from "../../utils";
import { isChildRecuperable, shouldSplitBalance } from "../../utils/balances";
import RefacturationsFieldArray from "../Balance/RefacturationsFieldArray";
// @ts-expect-error pure js
import { TRANSACTION_CHILD_INITIAL_VALUE } from "../../models/transactionChild";
import styles from "./TransactionSplitFieldArray.module.css";

interface TransactionSplitFieldArrayProps {
  name: string;
  /** Plafond de domaines des cibles proposées ; sans plafond, tout le portefeuille. */
  restrictToDomains?: string[];
}

interface TransactionChild {
  type?: string;
  domaine?: string;
  id_bien?: number;
  id_property_folder?: string;
  montant?: string;
  periode_debut?: string;
  periode_fin?: string;
}

export default function TransactionSplitFieldArray({
  name,
  restrictToDomains,
}: TransactionSplitFieldArrayProps) {
  const { values, setFieldValue } =
    useFormikContext<Record<string, TransactionChild[]>>();
  const { propertyFoldersById } = usePropertyFolders({ withById: true });
  const { balanceTypesByCategory, isLoading, balanceTypes } = useBalanceTypes();
  // Tout est ouvert par défaut (y compris les entrées ajoutées après coup,
  // manuellement ou via un modèle de répartition) : on mémorise les panneaux
  // fermés plutôt que les ouverts.
  const [closedValues, setClosedValues] = useState<Set<number>>(new Set());

  if (isLoading) return null;

  const allIndexes = (values[name] ?? []).map((_, index) => index);
  const openValue = allIndexes.filter((index) => !closedValues.has(index));

  function handleOpenChange(next: (string | number)[]) {
    const nextOpen = new Set(next);
    setClosedValues(
      new Set(allIndexes.filter((index) => !nextOpen.has(index))),
    );
  }

  return (
    <FieldArray
      name={name}
      render={(arrayHelpers) => (
        <div className={styles.entries}>
          <Accordion
            value={openValue}
            onValueChange={handleOpenChange}
            multiple
          >
            {values[name].map((child, index) => {
              const hasPeriod = balanceTypes[child.type]?.has_period;
              const shouldSplit = shouldSplitBalance(
                child.domaine,
                child.id_property_folder,
                propertyFoldersById,
              );
              const amountSignMatches =
                parseFloat(child.montant ?? "") ===
                setBalanceAmountSignFromType(child, balanceTypesByCategory);
              const showRefacturations =
                isChildRecuperable(child, balanceTypes) && !shouldSplit;

              return (
                <AccordionItem
                  key={index}
                  value={index}
                  variant="card"
                  title={`Répartition ${index + 1}`}
                  headerActions={
                    <>
                      <Text as="span" variant="deci" weight="bold">
                        {currencyFormatter(Number(child.montant) || 0)}
                      </Text>
                      <IconButton
                        icon={IconTrash}
                        label="Supprimer cette répartition"
                        onClick={() => {
                          arrayHelpers.remove(index);
                          // Les items sont identifiés par index : décale les
                          // panneaux fermés situés après l'item supprimé.
                          setClosedValues(
                            (prev) =>
                              new Set(
                                [...prev]
                                  .filter((v) => v !== index)
                                  .map((v) => (v > index ? v - 1 : v)),
                              ),
                          );
                        }}
                        variant="plain"
                        size="sm"
                      />
                    </>
                  }
                >
                  <div className={styles.entryBody}>
                    <div className={styles.row}>
                      <TextInput
                        type="text"
                        name={`${name}[${index}].comment`}
                        placeholder="Commentaire"
                        label="Libellé de l'entrée"
                        marginForm={""}
                        mandatory
                      />
                      <TextInput
                        type="number"
                        name={`${name}[${index}].montant`}
                        placeholder="0,00"
                        label="Montant"
                        mandatory
                        min={null}
                        unit={"€"}
                        step={"0.01"}
                        size="sm"
                        marginForm={""}
                      />
                    </div>

                    <BalanceTargetFields
                      prefix={`${name}[${index}].`}
                      {...BANK_AFFECTATION_TYPE_FILTERS}
                      restrictToDomains={restrictToDomains}
                      withFolders
                      onAffectationChange={(
                        balanceDomain: string,
                        parentId: number,
                      ) => {
                        if (
                          !shouldSplitBalance(
                            balanceDomain,
                            parentId,
                            propertyFoldersById,
                          )
                        )
                          setFieldValue(`${name}[${index}].balances`, []);
                      }}
                    />

                    {hasPeriod && (
                      <div className={styles.periodRow}>
                        <TextInput
                          type="date"
                          name={`${name}[${index}].periode_debut`}
                          label={"Période du"}
                          marginForm={""}
                          mandatory
                        />
                        <TextInput
                          type="date"
                          name={`${name}[${index}].periode_fin`}
                          label={"au"}
                          marginForm={""}
                          mandatory
                        />
                      </div>
                    )}

                    {shouldSplit && (
                      <div className={styles.repartitionSection}>
                        <BalanceSplitFields
                          name={`${name}[${index}].balances`}
                          parentId={parseInt(child.id_property_folder ?? "")}
                          domaineBalance={BalanceDomain.FOLDER}
                          totalAmountCents={Math.trunc(
                            Number(child.montant) * 100,
                          )}
                          hasPeriod={hasPeriod}
                          periodeDebut={child.periode_debut}
                          periodeFin={child.periode_fin}
                        />
                      </div>
                    )}

                    {!amountSignMatches && (
                      <Callout variant="danger">
                        Attention, vous ne devez pas affecter un montant positif
                        à une catégorie de type "appel" ou une charge. Pour un
                        paiement de loyer, sélectionnez la catégorie "paiement".
                      </Callout>
                    )}

                    {showRefacturations && (
                      <RefacturationsFieldArray
                        name={`${name}[${index}].refacturation_values`}
                        propertyId={child.id_bien}
                      />
                    )}
                  </div>
                </AccordionItem>
              );
            })}
          </Accordion>

          <Button
            type="button"
            variant="secondary"
            icon={IconPlus}
            onClick={() => arrayHelpers.push(TRANSACTION_CHILD_INITIAL_VALUE)}
          >
            Ajouter une entrée
          </Button>
        </div>
      )}
    />
  );
}
